const SQRT_2_PI = Math.sqrt(2 * Math.PI);

function normalCdf(x: number): number {
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989423 * Math.exp(-x * x / 2);
  const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  return x > 0 ? 1 - p : p;
}

function normalPdf(x: number): number {
  return Math.exp(-0.5 * x * x) / SQRT_2_PI;
}

export function implyVolatility(type: 'CE' | 'PE', marketPrice: number, S: number, K: number, T: number, r: number = 0.07): number {
  if (T <= 0 || marketPrice <= 0) return 0.2;
  
  let sigma = 0.2;
  for (let i = 0; i < 10; i++) {
    const d1 = (Math.log(S / K) + (r + sigma * sigma / 2) * T) / (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);
    let price = 0;
    
    if (type === 'CE') {
      price = S * normalCdf(d1) - K * Math.exp(-r * T) * normalCdf(d2);
    } else {
      price = K * Math.exp(-r * T) * normalCdf(-d2) - S * normalCdf(-d1);
    }
    
    const vega = S * normalPdf(d1) * Math.sqrt(T);
    if (vega === 0) break;
    
    const diff = price - marketPrice;
    if (Math.abs(diff) < 0.01) break;
    
    sigma = sigma - diff / vega;
    if (sigma <= 0) { sigma = 0.01; break; }
  }
  return sigma;
}

export function calcGreeks(type: 'CE' | 'PE', S: number, K: number, T: number, r: number = 0.07, sigma: number) {
  if (T <= 0 || sigma <= 0) return { delta: 0, gamma: 0, theta: 0, vega: 0, iv: sigma };

  const d1 = (Math.log(S / K) + (r + sigma * sigma / 2) * T) / (sigma * Math.sqrt(T));
  const d2 = d1 - sigma * Math.sqrt(T);

  const pdfD1 = normalPdf(d1);
  const cdfD1 = normalCdf(d1);
  const cdfMinusD1 = normalCdf(-d1);
  const cdfD2 = normalCdf(d2);
  const cdfMinusD2 = normalCdf(-d2);

  const gamma = pdfD1 / (S * sigma * Math.sqrt(T));
  const vega = S * pdfD1 * Math.sqrt(T) / 100;

  let delta = 0, theta = 0;

  if (type === 'CE') {
    delta = cdfD1;
    theta = (-S * pdfD1 * sigma / (2 * Math.sqrt(T)) - r * K * Math.exp(-r * T) * cdfD2) / 365;
  } else {
    delta = cdfD1 - 1;
    theta = (-S * pdfD1 * sigma / (2 * Math.sqrt(T)) + r * K * Math.exp(-r * T) * cdfMinusD2) / 365;
  }

  return { delta, gamma, theta, vega, iv: sigma };
}

// Helper to calculate time to expiry in years
export function calculateTimeToExpiry(expiryDateStr: string): number {
  if (!expiryDateStr) return 1 / 365; // 0DTE default
  
  // Parse '06JUN2026' or '06JUN26'
  const dayStr = expiryDateStr.slice(0, 2);
  const monthStr = expiryDateStr.slice(2, 5);
  let yearStr = expiryDateStr.slice(5);
  if (yearStr.length === 2) yearStr = '20' + yearStr;
  
  const months: Record<string, number> = {
    JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
    JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11
  };
  
  const expiryDate = new Date(parseInt(yearStr), months[monthStr], parseInt(dayStr), 15, 30, 0);
  const now = new Date();
  
  const diffMs = expiryDate.getTime() - now.getTime();
  if (diffMs <= 0) return 0.0001; // very small T
  
  return diffMs / (1000 * 60 * 60 * 24 * 365);
}
